import { AdvancedMarker } from '@vis.gl/react-google-maps'
import { groupBy, isEmpty, keys, min } from 'lodash'
import { ReactElement, useCallback, useState, MouseEvent } from 'react'
import styled from 'styled-components'

import { OverlappingStopsIcon } from 'components/Icons/OverlappingStopsIcon'
import { LocationPin } from 'components/Map/LocationPin'
import { IDatasetLocationProps } from 'features/RoutingProjects/types'
import { addLightToRgbColor, rgbToHex } from 'utils/style'
import { StopIndex } from 'components/Map/StopIndex'
import { usePlanContext } from 'features/RoutingProjects/containers/PlanContainer'
import { CloseIcon } from 'components/Icons'
import { coordinatesToString } from 'utils/map'

const DEFAULT_STOP_BG_COLOR = 'rgb(255, 255, 255)'
const DEFAULT_STOP_COLOR = 'rgb(44, 115, 255)'

const StopInfoWindowWrapper = styled.div`
  position: absolute;
  top: -5px;
  left: 50%;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0px 7px 7px 0px rgba(110, 136, 181, 0.2);
  background-color: #ffffff;
  z-index: 1;
  transform: translate(-50%, -100%);
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StopInfoWindowContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  white-space: nowrap;
`

const StopInfoWindowHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`

const StopInfoWindowRouteName = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #28265d;
`

const StopInfoWindowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StopInfoWindowCloseAction = styled.div`
  display: flex;
  margin: -4px;
  padding: 4px;
  cursor: pointer;
`

const StyledCloseIcon = styled(CloseIcon)`
  width: 20px;
  height: 20px;
  fill: #916ee8;

  &:hover {
    opacity: 0.7;
  }
`
const RoutesList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const RouteItem = styled.li`
  display: flex;
  gap: 8px;
  align-items: center;
`

const StopIndexContainer = styled.div`
  display: flex;
  gap: 4px;
`

const StopIndexList = styled.div<{ maxColumns: number }>`
  display: grid;
  gap: 4px;
  grid-template-columns: repeat(${({ maxColumns }): number => maxColumns}, 1fr);
`

const StopInfoWindowStopLabel = styled.div`
  color: #717171;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
`

const StopInfoWindowStopCoordinates = styled.div`
  font-size: 12px;
  font-weight: 400;
  line-height: 130%;
  color: #717171;
  white-space: nowrap;
`

interface IStopInfoWindowProps {
  stops: IDatasetLocationProps['stops']
  onClose: () => void
}

const StopInfoWindow = ({ stops, onClose }: IStopInfoWindowProps): ReactElement => {
  const stopsGroupedById = groupBy(stops, 'stopId')

  const handleClose = (event: MouseEvent): void => {
    event.stopPropagation()
    onClose()
  }

  const handleStopInfoWindowClick = (event: MouseEvent): void => {
    event.stopPropagation()
  }

  let routeName = ''
  const routesGroupedById = groupBy(stops, 'route.id')
  if (Object.keys(routesGroupedById).length === 1) {
    routeName = stops[0].route?.name || ''
  }

  return (
    <StopInfoWindowWrapper onClick={handleStopInfoWindowClick}>
      <StopInfoWindowContainer>
        <StopInfoWindowHeader>
          <StopInfoWindowRouteName>{routeName}</StopInfoWindowRouteName>
          <StopInfoWindowActions>
            <StopInfoWindowCloseAction onClick={handleClose}>
              <StyledCloseIcon />
            </StopInfoWindowCloseAction>
          </StopInfoWindowActions>
        </StopInfoWindowHeader>
        <RoutesList>
          {keys(stopsGroupedById).map((stopId) => {
            const stopsWithSameId = stopsGroupedById[stopId] || []
            const firstStop = stopsWithSameId.at(0)

            return (
              <RouteItem key={stopId}>
                <StopIndexContainer>
                  <StopIndexList maxColumns={min([5, stopsWithSameId.length]) as number}>
                    {stopsWithSameId.map((stop, index) => (
                      <StopIndex
                        key={index}
                        color={stop.rgbColor}
                        bgColor={stop.rgbColor ? `${rgbToHex(stop.rgbColor)}20` : undefined}
                      >
                        {stop.stopIndex + 1}
                      </StopIndex>
                    ))}
                  </StopIndexList>
                </StopIndexContainer>
                <div>
                  <StopInfoWindowStopLabel>{firstStop?.label}</StopInfoWindowStopLabel>
                  <StopInfoWindowStopCoordinates>
                    {coordinatesToString({
                      latitude: firstStop?.coordinates.latitude as number,
                      longitude: firstStop?.coordinates.longitude as number
                    })}
                  </StopInfoWindowStopCoordinates>
                </div>
              </RouteItem>
            )
          })}
        </RoutesList>
      </StopInfoWindowContainer>
    </StopInfoWindowWrapper>
  )
}

const MarkerWrapper = styled.div`
  position: relative;
`

const LocationsContainer = styled.div`
  display: flex;
  position: relative;
`

const OverlappingLocationContainer = styled.div<{ index: number }>`
  position: ${({ index }): string => (index === 0 ? 'relative' : 'absolute')};
  left: 0;
  top: 0;
  transform: translateX(${({ index }): number => index * 100}%);
`

const StyledOverlappingStopsIcon = styled(OverlappingStopsIcon)<{ focused: boolean }>`
  fill: ${DEFAULT_STOP_COLOR};
  width: ${({ focused }): string => (focused ? '22px' : '19px')};
  height: ${({ focused }): string => (focused ? '22px' : '19px')};
`

interface IFeatureMarkerProps {
  position: google.maps.LatLngLiteral
  stops: IDatasetLocationProps['stops']
  stopInfoWindowOpenedCount: number
  setStopInfoWindowOpenedCount: (count: number) => void
}

export const FeatureMarker = ({
  position,
  stops,
  stopInfoWindowOpenedCount,
  setStopInfoWindowOpenedCount
}: IFeatureMarkerProps): ReactElement | null => {
  let markerEl: ReactElement = <></>
  const [isHovered, setIsHovered] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [stopInfoWindowZIndex, setStopInfoWindowZIndex] = useState(0)
  const { handleLocationsCoordinatesUpdate } = usePlanContext()

  const handleMarkerClick = (): void => {
    if (!isFocused) {
      setStopInfoWindowZIndex(stopInfoWindowOpenedCount + 1)
      setStopInfoWindowOpenedCount(stopInfoWindowOpenedCount + 1)
    }
    setIsFocused(!isFocused)
  }
  const handleMarkerMouseEnter = useCallback(() => setIsHovered(true), [])
  const handleMarkerMouseLeave = useCallback(() => {
    setIsHovered(false)
  }, [])

  if (!stops || isEmpty(stops)) return null

  const firstStop = stops.at(0)
  const stopColor = firstStop?.rgbColor || DEFAULT_STOP_BG_COLOR
  const bgColor = isHovered ? addLightToRgbColor(stopColor, 0.15) : stopColor
  const borderColor = firstStop?.rgbColor ? '#fff' : DEFAULT_STOP_COLOR
  const textColor = firstStop?.rgbColor ? '#fff' : DEFAULT_STOP_COLOR
  const hasDestinationOrOriginStop = stops.some((stop) => stop.isDestination || stop.isOrigin)

  if (!firstStop) return null
  if (stops.length > 1) {
    if (hasDestinationOrOriginStop) {
      markerEl = (
        <LocationPin bgColor={DEFAULT_STOP_BG_COLOR} borderColor={DEFAULT_STOP_COLOR} big focused={isFocused}>
          <StyledOverlappingStopsIcon focused={isFocused} />
        </LocationPin>
      )
    } else {
      markerEl = (
        <LocationsContainer>
          {stops.map((stop, index) => {
            const stopColor = stop.rgbColor || DEFAULT_STOP_BG_COLOR
            const bgColor = isHovered ? addLightToRgbColor(stopColor, 0.15) : stopColor
            const borderColor = stop.rgbColor ? '#fff' : DEFAULT_STOP_COLOR
            const textColor = stop.rgbColor ? '#fff' : DEFAULT_STOP_COLOR

            return (
              <OverlappingLocationContainer key={index} index={index}>
                <LocationPin bgColor={bgColor} borderColor={borderColor} textColor={textColor} focused={isFocused}>
                  {stop.stopIndex + 1}
                </LocationPin>
              </OverlappingLocationContainer>
            )
          })}
        </LocationsContainer>
      )
    }
  } else {
    markerEl = (
      <LocationPin bgColor={bgColor} borderColor={borderColor} textColor={textColor} focused={isFocused}>
        {firstStop.stopIndex + 1}
      </LocationPin>
    )
  }

  const handleDragEnd = (event: google.maps.MapMouseEvent): void => {
    if (event.latLng) {
      handleLocationsCoordinatesUpdate({
        locationIds: stops.map((stop) => stop.stopId),
        coordinates: {
          latitude: event.latLng.lat(),
          longitude: event.latLng.lng()
        }
      })
    }
  }

  const handleCloseStopInfoWindow = (): void => {
    setIsFocused(false)
  }
  let zIndex = 0
  if (isFocused) {
    zIndex = stopInfoWindowZIndex
  } else if (hasDestinationOrOriginStop) {
    zIndex = 1
  }

  return (
    <>
      <AdvancedMarker
        position={position}
        onClick={handleMarkerClick}
        onMouseEnter={handleMarkerMouseEnter}
        onMouseLeave={handleMarkerMouseLeave}
        zIndex={zIndex}
        onDragEnd={handleDragEnd}
      >
        <MarkerWrapper>
          {markerEl}
          {isFocused && <StopInfoWindow stops={stops} onClose={handleCloseStopInfoWindow} />}
        </MarkerWrapper>
      </AdvancedMarker>
    </>
  )
}
